import React, { useMemo, useState } from "react";
import { Box, Chip, Skeleton, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import PlayerHeadshot from "../PlayerHeadshot";
import HomeSection from "../home/HomeSection";
import { homeCardHover, horizontalScroll } from "../home/homeStyles";
import { colors } from "../../theme";

const STAT_TABS = [
  { key: "points", label: "Points", statLabel: "Franchise PTS" },
  { key: "rebounds", label: "Rebounds", statLabel: "Franchise REB" },
  { key: "assists", label: "Assists", statLabel: "Franchise AST" },
];

const NbaTeamLegendCard = ({ player, statField, statLabel, onClick }) => {
  const value = player[statField] ?? "–";
  const position = player.position;

  return (
    <Box
      onClick={onClick}
      sx={{
        minWidth: 140,
        maxWidth: 140,
        flex: "0 0 auto",
        p: 1.5,
        borderRadius: 1.5,
        border: `1px solid ${colors.border}`,
        ...homeCardHover,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
        <PlayerHeadshot
          playerId={player.playerId}
          sport="nba"
          size={56}
          showTooltip={false}
          showLoading={false}
        />
      </Box>
      <Typography
        variant="body2"
        fontWeight={600}
        noWrap
        textAlign="center"
        title={player.name}
      >
        {player.name}
      </Typography>
      {position ? (
        <Typography
          variant="caption"
          color="text.secondary"
          noWrap
          display="block"
          textAlign="center"
        >
          {position}
        </Typography>
      ) : null}
      <Box sx={{ mt: 1, textAlign: "center" }}>
        <Typography variant="caption" color="text.secondary">
          {statLabel}
        </Typography>
        <Typography
          variant="h6"
          fontWeight={700}
          color="primary.main"
          lineHeight={1.2}
        >
          {Number(value).toLocaleString()}
        </Typography>
      </Box>
    </Box>
  );
};

const NbaTeamLegendsRow = ({ franchiseName, leaders = {}, isLoading }) => {
  const navigate = useNavigate();
  const [activeStat, setActiveStat] = useState("points");
  const activeTab = STAT_TABS.find((tab) => tab.key === activeStat) || STAT_TABS[0];
  const players = leaders[activeStat] || [];

  const action = useMemo(
    () => (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, justifyContent: "flex-end" }}>
        {STAT_TABS.map((tab) => (
          <Chip
            key={tab.key}
            label={tab.label}
            size="small"
            clickable
            color={tab.key === activeStat ? "primary" : "default"}
            variant={tab.key === activeStat ? "filled" : "outlined"}
            onClick={() => setActiveStat(tab.key)}
          />
        ))}
      </Box>
    ),
    [activeStat]
  );

  return (
    <HomeSection
      title="Franchise legends"
      subtitle={`Top ${franchiseName} careers by stat — totals with this franchise only`}
      action={action}
      noPadding
    >
      <Box sx={{ p: 2 }}>
        {isLoading ? (
          <Box sx={horizontalScroll}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton
                key={i}
                variant="rounded"
                width={140}
                height={160}
                sx={{ flexShrink: 0 }}
              />
            ))}
          </Box>
        ) : null}

        {!isLoading && players.length === 0 ? (
          <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
            No franchise leader data yet
          </Typography>
        ) : null}

        {!isLoading && players.length > 0 ? (
          <Box sx={horizontalScroll}>
            {players.map((player) => (
              <NbaTeamLegendCard
                key={player.playerId}
                player={player}
                statField={activeStat}
                statLabel={activeTab.statLabel}
                onClick={() => navigate(`/app/nba/player/${player.playerId}`)}
              />
            ))}
          </Box>
        ) : null}
      </Box>
    </HomeSection>
  );
};

export default NbaTeamLegendsRow;

export function collectFranchiseLegendIds(leaders = {}) {
  const ids = new Set();
  STAT_TABS.forEach((tab) => {
    (leaders[tab.key] || []).forEach((player) => {
      if (player?.playerId) ids.add(player.playerId);
    });
  });
  return ids;
}
